import React, { useState, useEffect } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    required,
    Toolbar,
    SaveButton,
    useRecordContext,
    useDelete,
    useNotify,
    useRedirect,
    useTranslate,
    useRefresh,
    useDataProvider,
} from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    IconButton,
    Divider,
    InputAdornment,
    Chip,
    Tooltip,
    Select,
    MenuItem,
} from '@mui/material';
import {
    DnsRounded,
    Settings,
    Build,
    Security,
    CloudQueue,
    Warning,
    ArrowBack,
    Delete,
    Refresh,
    RestartAlt,
    VpnKey,
    Visibility,
    VisibilityOff,
    ContentCopy,
    AutoAwesome,
    Storage,
    CheckCircle,
    BarChart,
} from '@mui/icons-material';
import { CapabilitiesInput } from './CapabilitiesInput';
import { ServerConfigInput } from './ServerConfigInput';
import { IpWhitelistFieldInput } from './IpWhitelistFieldInput';
import RestartServerDialog from './RestartServerDialog';
import { generateOcppServerKeys, copyToClipboard } from '../../utils/keyGeneration';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// Same aliases as Create — backend preprocesses camelCase -> snake_case before
// Zod validates, so error paths come back in snake_case for endpoint_url and
// ip_whitelist. The form uses camelCase for those two sources.
const OCPP_SERVER_PATH_ALIAS: Record<string, string> = {
    endpoint_url: 'endpointUrl',
    ip_whitelist: 'ipWhitelist',
};

const mapOcppServerBackendPath = (path: string): string => {
    const stripped = path
        .replace(/^body\./, '')
        .replace(/^params\./, '')
        .replace(/^query\./, '');
    return OCPP_SERVER_PATH_ALIAS[stripped] || stripped;
};

const OCPP_SERVER_KNOWN_FIELDS = new Set([
    'name', 'description', 'endpointUrl', 'ocpp_to_backend_api_key',
    'backend_to_ocpp_api_key', 'status', 'capabilities', 'ipWhitelist',
    'config', 'elk_batch_size', 'elk_batch_timeout_ms', 'elk_max_buffer_size',
    'elk_spillover_enabled', 'elk_spillover_retention_days',
    'zero_energy_timeout_seconds', 'supabaseConnectionId',
]);

const ocppServerFieldLabelKey = (field: string): string =>
    OCPP_SERVER_KNOWN_FIELDS.has(field)
        ? `resources.ocpp-servers.fields.${field}`
        : field;

// Supabase Connection Manager Component
const SupabaseConnectionManager = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const [currentConnection, setCurrentConnection] = useState<any>(null);
    const [availableConnections, setAvailableConnections] = useState<any[]>([]);
    const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (record?.id) {
            loadCurrentConnection();
            loadAvailableConnections();
        }
    }, [record?.id]);

    const loadCurrentConnection = async () => {
        if (!record?.id) return;

        try {
            const { tokenManager } = await import('../../utils/tokenManager');
            const { API_CONFIG } = await import('../../config/api');
            const accessToken = await tokenManager.getAccessToken();

            const response = await fetch(
                `${API_CONFIG.baseURL}/api/backend/ocpp-servers/${record.id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                }
            );

            const result = await response.json();
            // API returns connections array with nested supabase_connections object
            if (result.data?.connections?.length > 0) {
                const connectionData = result.data.connections[0];
                setCurrentConnection(connectionData.supabase_connections || connectionData);
                setSelectedConnectionId(connectionData.supabase_connections?.id || '');
            }
        } catch (error) {
            console.error('Failed to load current connection:', error);
        }
    };

    const loadAvailableConnections = async () => {
        try {
            const { data } = await dataProvider.getList('supabase-connections', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: { is_active: true },
            });
            setAvailableConnections(data);
        } catch (error) {
            console.error('Failed to load available connections:', error);
        }
    };

    const handleConnectionChange = async () => {
        if (!record?.id || !selectedConnectionId) return;

        setLoading(true);
        try {
            const { tokenManager } = await import('../../utils/tokenManager');
            const { API_CONFIG } = await import('../../config/api');
            const accessToken = await tokenManager.getAccessToken();

            // If there's a current connection, unassign it first
            if (currentConnection) {
                await fetch(
                    `${API_CONFIG.baseURL}/api/backend/supabase-connections/${currentConnection.id}/unassign/${record.id}`,
                    {
                        method: 'DELETE',
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                        },
                    }
                );
            }

            // Assign the new connection
            const response = await fetch(
                `${API_CONFIG.baseURL}/api/backend/supabase-connections/${selectedConnectionId}/assign`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        ocppServerId: record.id,
                        isPrimary: true,
                    }),
                }
            );

            if (response.ok) {
                notify('Supabase connection updated successfully', { type: 'success' });
                setIsEditing(false);
                refresh();
                loadCurrentConnection();
            } else {
                throw new Error('Failed to update connection');
            }
        } catch (error) {
            console.error('Failed to update connection:', error);
            notify('Failed to update Supabase connection', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!currentConnection ? (
                // No connection state
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            No Supabase Connection
                        </Typography>
                        <Typography variant="body2">
                            This OCPP server requires a Supabase connection to manage charging data.
                        </Typography>
                    </Box>
                </Alert>
            ) : (
                // Has connection state
                <Box sx={{
                    border: '1px solid #E0E0E0',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#FAFAFA',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Current Connection
                        </Typography>
                        {!isEditing && (
                            <Button
                                size="small"
                                onClick={() => setIsEditing(true)}
                                sx={{ textTransform: 'none' }}
                            >
                                Change Connection
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Storage sx={{ color: '#00C853', flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                                {currentConnection.name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                <Chip
                                    label={currentConnection.environment}
                                    size="small"
                                    sx={{
                                        backgroundColor:
                                            currentConnection.environment === 'production'
                                                ? '#FFEBEE'
                                                : currentConnection.environment === 'staging'
                                                ? '#FFF3E0'
                                                : '#E3F2FD',
                                        color:
                                            currentConnection.environment === 'production'
                                                ? '#C62828'
                                                : currentConnection.environment === 'staging'
                                                ? '#F57C00'
                                                : '#1565C0',
                                    }}
                                />
                                {currentConnection.is_active && (
                                    <Chip
                                        icon={<CheckCircle sx={{ fontSize: 16 }} />}
                                        label="Active"
                                        size="small"
                                        sx={{
                                            backgroundColor: '#E8F5E9',
                                            color: '#00C853',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            {(isEditing || !currentConnection) && (
                <Box sx={{
                    border: '1px dashed #E0E0E0',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#FAFAFA',
                }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a Supabase Connection
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Select
                                value={selectedConnectionId}
                                onChange={(e) => setSelectedConnectionId(e.target.value)}
                                fullWidth
                                displayEmpty
                                sx={{ backgroundColor: '#FFFFFF' }}
                            >
                                <MenuItem value="">
                                    <em>Select a connection...</em>
                                </MenuItem>
                                {availableConnections.map((conn) => (
                                    <MenuItem key={conn.id} value={conn.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{conn.name}</span>
                                            <Chip
                                                label={conn.environment}
                                                size="small"
                                                sx={{
                                                    ml: 1,
                                                    height: 20,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleConnectionChange}
                                disabled={loading || !selectedConnectionId || selectedConnectionId === currentConnection?.id}
                                sx={{
                                    minWidth: 100,
                                    backgroundColor: '#00C853',
                                    '&:hover': {
                                        backgroundColor: '#00A843',
                                    },
                                }}
                            >
                                {loading ? 'Saving...' : 'Save'}
                            </Button>
                            {isEditing && (
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setIsEditing(false);
                                        setSelectedConnectionId(currentConnection?.id || '');
                                    }}
                                    disabled={loading}
                                    sx={{ minWidth: 100 }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const DeleteOcppServerSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const notify = useNotify();
    const redirect = useRedirect();
    const [deleteOne] = useDelete();
    const translate = useTranslate();

    const handleClick = () => {
        setOpen(true);
        setConfirmText('');
    };

    const handleClose = () => {
        setOpen(false);
        setConfirmText('');
    };

    const handleConfirm = () => {
        if (confirmText === record?.name) {
            void deleteOne(
                'ocpp-servers',
                { id: record.id, previousData: record },
                {
                    onSuccess: () => {
                        notify(translate('resources.ocpp-servers.notifications.deleteSuccess'));
                        redirect('/ocpp-servers');
                    },
                    onError: () => {
                        notify(translate('resources.ocpp-servers.notifications.deleteError'), { type: 'error' });
                    },
                }
            );
            setOpen(false);
        }
    };

    const hasLoadGroups = record?.loadGroupCount > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">
                    {hasLoadGroups
                        ? translate('resources.ocpp-servers.edit.cannotDeleteWithLoadGroups', { count: record?.loadGroupCount })
                        : translate('resources.ocpp-servers.edit.deleteServerDescription')}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                disabled={hasLoadGroups}
                sx={{
                    borderColor: hasLoadGroups ? '#BDBDBD' : '#F44336',
                    color: hasLoadGroups ? '#BDBDBD' : '#F44336',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                        borderColor: hasLoadGroups ? '#BDBDBD' : '#D32F2F',
                        backgroundColor: hasLoadGroups ? 'transparent' : 'rgba(244,67,54,0.08)',
                    },
                    '&.Mui-disabled': {
                        borderColor: '#BDBDBD',
                        color: '#BDBDBD',
                    },
                }}
                title={hasLoadGroups
                    ? translate('resources.ocpp-servers.edit.cannotDeleteWithLoadGroups', { count: record?.loadGroupCount })
                    : translate('common.delete')}
            >
                {translate('common.delete')}
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#F44336' }} />
                        <Typography variant="h6">
                            {translate('resources.ocpp-servers.edit.deleteConfirmTitle')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{translate('resources.ocpp-servers.edit.deleteConfirmWarning')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {translate('resources.ocpp-servers.edit.deleteConfirmMessage')} <strong>{record?.name}</strong>.
                            {' '}{translate('resources.ocpp-servers.edit.deleteConfirmInfo')}
                        </Typography>
                    </Alert>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {translate('resources.ocpp-servers.edit.deleteConfirmPrompt')} <strong>{record?.name}</strong> {translate('resources.ocpp-servers.edit.deleteConfirmPromptBelow')}
                    </Typography>

                    <TextField
                        fullWidth
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={translate('resources.ocpp-servers.edit.deleteConfirmTypePlaceholder', { name: record?.name })}
                        variant="outlined"
                        autoFocus
                        error={confirmText !== '' && confirmText !== record?.name}
                        helperText={
                            confirmText !== '' && confirmText !== record?.name
                                ? translate('resources.ocpp-servers.edit.nameDoesNotMatch')
                                : ''
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={handleClose}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={confirmText !== record?.name}
                        sx={{
                            backgroundColor: '#F44336',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#FFCDD2',
                            },
                        }}
                    >
                        {translate('resources.ocpp-servers.edit.deleteButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const SecurityActionsSection = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [regeneratedKeys, setRegeneratedKeys] = useState<{
        ocppToBackendApiKey?: string;
        backendToOcppApiKey?: string;
    } | null>(null);
    const [showRegeneratedKeys, setShowRegeneratedKeys] = useState(false);
    const [apiKeyCopied, setApiKeyCopied] = useState(false);
    const [backendToOcppApiKeyCopied, setBackendToOcppApiKeyCopied] = useState(false);
    const [regeneratedElkKey, setRegeneratedElkKey] = useState<string | null>(null);
    const [showElkKeyDialog, setShowElkKeyDialog] = useState(false);
    const [elkKeyCopied, setElkKeyCopied] = useState(false);
    const [showElkConfirmDialog, setShowElkConfirmDialog] = useState(false);

    const handleRegenerateKeys = async () => {
        try {
            // Generate new keys on frontend
            const newKeys = generateOcppServerKeys();

            // Send to backend for secure storage using direct API call
            const result = await apiClient.post(
                `${API_CONFIG.endpoints.backend.ocppServers}/${record?.id}/rotate-keys`,
                {
                    ocpp_to_backend_api_key: newKeys.ocppToBackendApiKey,
                    backend_to_ocpp_api_key: newKeys.backendToOcppApiKey
                }
            );

            // Store regenerated keys for display
            setRegeneratedKeys(newKeys);
            setShowRegeneratedKeys(true);
            setApiKeyCopied(false);
            setBackendToOcppApiKeyCopied(false);

            notify(translate('resources.ocpp-servers.notifications.keysRegenerated'), { type: 'success' });
            // Force a hard refresh to get the new data
            setTimeout(() => refresh(), 100);
        } catch (error) {
            console.error('Error regenerating keys:', error);
            notify(translate('resources.ocpp-servers.notifications.apiKeyRegenerateError'), { type: 'error' });
        }
    };

    const handleCopyApiKey = async () => {
        if (regeneratedKeys?.ocppToBackendApiKey) {
            await copyToClipboard(regeneratedKeys.ocppToBackendApiKey);
            setApiKeyCopied(true);
            notify(translate('resources.ocpp-servers.notifications.ocppToBackendApiKeyCopied'), { type: 'success' });
        }
    };

    const handleCopyBackendToOcppApiKey = async () => {
        if (regeneratedKeys?.backendToOcppApiKey) {
            await copyToClipboard(regeneratedKeys.backendToOcppApiKey);
            setBackendToOcppApiKeyCopied(true);
            notify(translate('resources.ocpp-servers.notifications.backendToOcppApiKeyCopied'), { type: 'success' });
        }
    };

    const handleRegenerateElkKey = async () => {
        try {
            // Call backend to regenerate ELK API key
            const result = await apiClient.post(
                `${API_CONFIG.endpoints.backend.ocppServers}/${record?.id}/regenerate-elk-key`,
                {}
            ) as { newElkApiKey: string; elkApiKeySuffix: string; message: string };

            // Store the new key and show dialog
            setRegeneratedElkKey(result.newElkApiKey);
            setShowElkKeyDialog(true);
            setElkKeyCopied(false);
            setShowElkConfirmDialog(false);

            notify('ELK API key regenerated successfully', { type: 'success' });
            // Force a refresh to update the suffix display
            setTimeout(() => refresh(), 100);
        } catch (error) {
            console.error('Error regenerating ELK API key:', error);
            notify('Failed to regenerate ELK API key', { type: 'error' });
        }
    };

    const handleCopyElkKey = async () => {
        if (regeneratedElkKey) {
            await copyToClipboard(regeneratedElkKey);
            setElkKeyCopied(true);
            notify('ELK API key copied to clipboard', { type: 'success' });
        }
    };

    const handleCloseElkKeyDialog = () => {
        setShowElkKeyDialog(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    {translate('resources.ocpp-servers.edit.securityWarning')}
                </Typography>
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    onClick={handleRegenerateKeys}
                    startIcon={<Refresh />}
                    sx={{
                        borderColor: '#FF9800',
                        color: '#FF9800',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        '&:hover': {
                            borderColor: '#F57C00',
                            backgroundColor: 'rgba(255,152,0,0.08)',
                        },
                    }}
                >
                    {translate('resources.ocpp-servers.edit.regenerateBothKeys')}
                </Button>

            </Box>

            {showRegeneratedKeys && regeneratedKeys && (
                <Box sx={{ mt: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Warning />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {translate('resources.ocpp-servers.edit.newKeysWarningTitle')}
                                </Typography>
                                <Typography variant="body2">
                                    {translate('resources.ocpp-servers.edit.newKeysWarningMessage')}
                                </Typography>
                            </Box>
                        </Box>
                    </Alert>

                    {/* New OCPP to Backend API Key */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            {translate('resources.ocpp-servers.edit.newOcppToBackendApiKey')}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}>
                            <Box sx={{ flex: 1, wordBreak: 'break-all', minWidth: 0 }}>
                                {regeneratedKeys.ocppToBackendApiKey}
                            </Box>
                            <Tooltip title={apiKeyCopied ? translate('resources.ocpp-servers.create.copied') : translate('resources.ocpp-servers.create.copyToClipboard')}>
                                <IconButton
                                    onClick={handleCopyApiKey}
                                    size="small"
                                    color={apiKeyCopied ? 'success' : 'default'}
                                >
                                    <ContentCopy />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>

                    {/* New Backend to OCPP API Key */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                            {translate('resources.ocpp-servers.edit.newBackendToOcppApiKey')}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            fontFamily: 'monospace',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        }}>
                            <Box sx={{ flex: 1, wordBreak: 'break-all', minWidth: 0 }}>
                                {regeneratedKeys.backendToOcppApiKey}
                            </Box>
                            <Tooltip title={backendToOcppApiKeyCopied ? translate('resources.ocpp-servers.create.copied') : translate('resources.ocpp-servers.create.copyToClipboard')}>
                                <IconButton
                                    onClick={handleCopyBackendToOcppApiKey}
                                    size="small"
                                    color={backendToOcppApiKeyCopied ? 'success' : 'default'}
                                >
                                    <ContentCopy />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* ELK API Key Section */}
            <Divider sx={{ my: { xs: 2, sm: 3 } }} />
            <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                    {translate('resources.ocpp-servers.edit.elkKeySection', 'ELK Stack API Key')}
                </Typography>

                {record?.elk_key_display ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{
                            border: '1px solid #E0E0E0',
                            borderRadius: 2,
                            p: 2,
                            backgroundColor: '#FAFAFA',
                        }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Current ELK API Key
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {record.elk_key_display}
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            onClick={() => setShowElkConfirmDialog(true)}
                            startIcon={<Refresh />}
                            sx={{
                                borderColor: '#FF9800',
                                color: '#FF9800',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: { xs: 2, sm: 3 },
                                py: 1.5,
                                alignSelf: 'flex-start',
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                '&:hover': {
                                    borderColor: '#F57C00',
                                    backgroundColor: 'rgba(255,152,0,0.08)',
                                },
                            }}
                        >
                            {translate('resources.ocpp-servers.edit.regenerateElkKey', 'Regenerate ELK API Key')}
                        </Button>
                    </Box>
                ) : (
                    <Alert severity="info">
                        <Typography variant="body2">
                            {translate('resources.ocpp-servers.edit.noElkKeyConfigured', 'No ELK API key configured for this server. ELK logging is disabled.')}
                        </Typography>
                    </Alert>
                )}
            </Box>

            {/* ELK Key Confirmation Dialog */}
            <Dialog
                open={showElkConfirmDialog}
                onClose={() => setShowElkConfirmDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#FF9800' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {translate('resources.ocpp-servers.edit.confirmElkKeyRegeneration', 'Confirm ELK Key Regeneration')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            {translate('resources.ocpp-servers.edit.elkKeyRegenerationWarning', 'Regenerating the ELK API key will immediately invalidate the old key. The OCPP server will need to be restarted with the new key to continue logging to Elasticsearch.')}
                        </Typography>
                    </Alert>
                    <Typography variant="body2">
                        {translate('resources.ocpp-servers.edit.elkKeyRegenerationConfirm', 'Are you sure you want to regenerate the ELK API key?')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowElkConfirmDialog(false)}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleRegenerateElkKey}
                        variant="contained"
                        sx={{
                            backgroundColor: '#FF9800',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#F57C00',
                            },
                        }}
                    >
                        {translate('resources.ocpp-servers.edit.confirmRegenerate', 'Yes, Regenerate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ELK Key Display Dialog */}
            <Dialog
                open={showElkKeyDialog}
                onClose={handleCloseElkKeyDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {translate('resources.ocpp-servers.elkKey.title', 'Save New ELK API Key')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            {translate('resources.ocpp-servers.elkKey.warningTitle', 'Important: Save this key now!')}
                        </Typography>
                        <Typography variant="body2">
                            {translate('resources.ocpp-servers.elkKey.warningMessage', 'This new ELK API key will only be shown once and cannot be retrieved later. The old key has been invalidated.')}
                        </Typography>
                    </Alert>

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                        {translate('resources.ocpp-servers.elkKey.label', 'New Elasticsearch API Key')}
                    </Typography>
                    <Box sx={{
                        backgroundColor: '#F5F5F5',
                        p: 2,
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        position: 'relative',
                    }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {regeneratedElkKey}
                        </Typography>
                        <Tooltip title={elkKeyCopied ? translate('resources.ocpp-servers.create.copied', 'Copied!') : translate('resources.ocpp-servers.create.copyToClipboard', 'Copy to clipboard')}>
                            <IconButton
                                onClick={handleCopyElkKey}
                                size="small"
                                color={elkKeyCopied ? 'success' : 'default'}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                }}
                            >
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        {translate('resources.ocpp-servers.elkKey.helperText', 'Update your OCPP server configuration with this new key and restart the server.')}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseElkKeyDialog}
                        variant="contained"
                        color="primary"
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('resources.ocpp-servers.elkKey.confirmButton', 'I have saved the key')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: { xs: 2, sm: 4 },
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();
    const [showRestartDialog, setShowRestartDialog] = useState(false);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/ocpp-servers')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.ocpp-servers.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {translate('resources.ocpp-servers.edit.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                    {record?.name} • {record?.endpointUrl} • {record?.status}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                color="warning"
                startIcon={<RestartAlt />}
                onClick={() => setShowRestartDialog(true)}
                disabled={!record?.id}
                sx={{
                    flexShrink: 0,
                    borderColor: '#FF9800',
                    color: '#FF9800',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    '&:hover': {
                        borderColor: '#F57C00',
                        backgroundColor: 'rgba(255,152,0,0.08)',
                    },
                }}
            >
                Restart Server
            </Button>
            <RestartServerDialog
                open={showRestartDialog}
                serverId={String(record?.id || '')}
                serverName={String(record?.name || '')}
                onClose={() => setShowRestartDialog(false)}
            />
        </Box>
    );
};

export const OcppServersEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [showInboundApiKey, setShowInboundApiKey] = useState(false);
    const [showOutboundApiKey, setShowOutboundApiKey] = useState(false);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

    // Validation rules
    const validateRequired = [required()];
    const validateUrl = [
        required(),
        (value: string) => {
            try {
                new URL(value);
                return undefined;
            } catch {
                return translate('resources.ocpp-servers.validation.invalidUrl');
            }
        }
    ];

    return (
        <Edit
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.ocpp-servers.notifications.updateSuccess', { type: 'success' });
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapOcppServerBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(ocppServerFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 } }}>
                    <PageHeader />

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <DnsRounded sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.ocpp-servers.edit.nameHelperText')}
                                />
                                <TextInput
                                    source="description"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    helperText={translate('resources.ocpp-servers.edit.descriptionHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Connection Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <CloudQueue sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.connectionConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="endpoint_url"
                                    validate={validateUrl}
                                    fullWidth
                                    helperText={translate('resources.ocpp-servers.edit.endpointUrlHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Supabase Connection Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Storage sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.supabaseConnection')}
                                </Typography>
                            </Box>

                            <SupabaseConnectionManager />
                        </CardContent>
                    </Card>

                    {/* Security Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Security sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.securityConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.fields.ocppToBackendApiKey')}
                                    </Typography>
                                    <TextInput
                                        source="api_key_display"
                                        fullWidth
                                        disabled
                                        helperText={translate('resources.ocpp-servers.edit.ocppToBackendApiKeyHelperText')}
                                        InputProps={{
                                            sx: { fontFamily: 'monospace' },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <VpnKey sx={{ color: '#00C853' }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                                <Alert severity="info">
                                    <Typography variant="body2">
                                        {translate('resources.ocpp-servers.edit.keysCannotBeRetrieved')}
                                    </Typography>
                                </Alert>
                            </Box>

                            <Divider sx={{ my: { xs: 2, sm: 3 } }} />

                            <SecurityActionsSection />
                        </CardContent>
                    </Card>

                    {/* Status Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Build sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.statusConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectInput
                                    source="status"
                                    choices={[
                                        { id: 'pending', name: 'Pending' },
                                        { id: 'active', name: 'Active' },
                                        { id: 'inactive', name: 'Inactive' },
                                        { id: 'maintenance', name: 'Maintenance' },
                                        { id: 'retired', name: 'Retired' },
                                    ]}
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.ocpp-servers.edit.statusHelperText')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Advanced Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Settings sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.advancedConfig')}
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    {translate('resources.ocpp-servers.edit.advancedConfigMessage')}
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.capabilities.title')}
                                    </Typography>
                                    <CapabilitiesInput source="capabilities" />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.config.title')}
                                    </Typography>
                                    <ServerConfigInput source="config" />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle1" sx={{ mb: { xs: 1, sm: 2 }, fontWeight: 500 }}>
                                        {translate('resources.ocpp-servers.ipWhitelist.title')}
                                    </Typography>
                                    <IpWhitelistFieldInput source="ipWhitelist" />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* ELK Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <BarChart sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.elkConfig.title')}
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                                <Typography variant="body2">
                                    {translate('resources.ocpp-servers.elkConfig.description')}
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <WheelSafeNumberInput
                                    source="elk_batch_size"
                                    fullWidth
                                    min={10}
                                    max={1000}
                                    defaultValue={100}
                                    helperText={translate('resources.ocpp-servers.elkConfig.batchSizeHelper')}
                                />
                                <WheelSafeNumberInput
                                    source="elk_batch_timeout_ms"
                                    fullWidth
                                    min={1000}
                                    max={300000}
                                    defaultValue={60000}
                                    helperText={translate('resources.ocpp-servers.elkConfig.batchTimeoutHelper')}
                                />
                                <WheelSafeNumberInput
                                    source="elk_max_buffer_size"
                                    fullWidth
                                    min={1000}
                                    max={100000}
                                    defaultValue={10000}
                                    helperText={translate('resources.ocpp-servers.elkConfig.maxBufferSizeHelper')}
                                />
                                <BooleanInput
                                    source="elk_spillover_enabled"
                                    defaultValue={true}
                                    helperText={translate('resources.ocpp-servers.elkConfig.spilloverEnabledHelper')}
                                />
                                <WheelSafeNumberInput
                                    source="elk_spillover_retention_days"
                                    fullWidth
                                    min={1}
                                    max={30}
                                    defaultValue={7}
                                    helperText={translate('resources.ocpp-servers.elkConfig.spilloverRetentionHelper')}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Session Management Configuration Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '& .MuiFormControl-root': { width: '100%' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <AutoAwesome sx={{ mr: 1.5, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    Session Management
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                                <Typography variant="body2">
                                    Configure automatic handling of charging sessions when EV is not ready to charge.
                                </Typography>
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <WheelSafeNumberInput
                                    source="zero_energy_timeout_seconds"
                                    label="Zero-Energy Timeout (seconds)"
                                    fullWidth
                                    min={30}
                                    max={600}
                                    defaultValue={120}
                                    helperText="Time to wait before auto-canceling a session when no energy is delivered (30-600 seconds, default: 120)"
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Delete Server Section */}
                    <Card sx={{ mt: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
                                <Delete sx={{ mr: 1.5, color: '#F44336' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.ocpp-servers.edit.deleteServer')}
                                </Typography>
                            </Box>

                            <DeleteOcppServerSection />
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};